import extractErrorMessage from '../../../shared/utils/extractErrorMessage';
import { authClient } from '../../../shared/api/client';

const isOkStatus = (status) => status >= 200 && status < 300;
const toBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result || '');
    reader.onerror = () => reject(new Error('Neuspesno citanje fajla.'));
    reader.readAsDataURL(file);
  });

export const getTherapyModalContextFromResponse = (data) =>
  data?.therapy ||
  data?.therapyContext ||
  data?.data?.therapy ||
  data?.data?.therapyContext ||
  null;

export const getTherapyReservationFromResponse = (data) => {
  if (Object.prototype.hasOwnProperty.call(data || {}, 'reservation')) {
    return data.reservation;
  }

  if (Object.prototype.hasOwnProperty.call(data?.data || {}, 'reservation')) {
    return data.data.reservation;
  }

  return undefined;
};

export const mergeTherapyContextReservation = (
  therapyContext,
  activeReservation
) => {
  if (!therapyContext) return therapyContext;

  if (!activeReservation) {
    return {
      ...therapyContext,
      reservation: null,
      reservationActivation: null,
    };
  }

  if (activeReservation.canApply) {
    return {
      ...therapyContext,
      reservation: null,
      reservationActivation: activeReservation,
    };
  }

  return {
    ...therapyContext,
    reservation: activeReservation,
    reservationActivation: null,
  };
};

// Filteri liste terapija kao query parametri (bez page/limit). Isti filteri
// idu i na listu i na "selektuj sve", pa obe vracaju isti skup terapija.
const buildTherapyFilterQuery = ({
  search = null,
  userSearch = null,
  medications = [],
  medicationMatch = null,
  pickupMethod = null,
  status = null,
  occurrenceStatus = null,
  startDate = null,
  endDate = null,
  lboSearch = null,
  bzkSearch = null,
  phoneSearch = null,
  renewStartDate = null,
  renewEndDate = null,
  userBirthdaySearch = null,
  haveApp = null,
  isHealthBookletVerified = null,
} = {}) => {
  let query = '';
  if (search) {
    query += `&search=${encodeURIComponent(search)}`;
  }
  if (userSearch) {
    query += `&userSearch=${encodeURIComponent(userSearch)}`;
  }
  // Svaki izabrani lek ide kao zaseban `medications` parametar - nazivi
  // mogu da sadrze zarez, pa se ne spajaju u jedan string.
  (Array.isArray(medications) ? medications : [])
    .filter(Boolean)
    .forEach((name) => {
      query += `&medications=${encodeURIComponent(name)}`;
    });
  // Nacin poklapanja lekova ('any' | 'exact') - salje se samo uz izabrane lekove.
  if (medicationMatch && medications?.length) {
    query += `&medicationMatch=${encodeURIComponent(medicationMatch)}`;
  }
  if (pickupMethod) {
    const pickupMethods = (
      Array.isArray(pickupMethod) ? pickupMethod : [pickupMethod]
    ).filter(Boolean);
    if (pickupMethods.length) {
      query += `&pickupMethod=${pickupMethods
        .map((value) => encodeURIComponent(value))
        .join(',')}`;
    }
  }
  if (status) {
    query += `&status=${encodeURIComponent(status)}`;
  }
  if (occurrenceStatus) {
    const occurrenceStatuses = (
      Array.isArray(occurrenceStatus) ? occurrenceStatus : [occurrenceStatus]
    ).filter(Boolean);
    if (occurrenceStatuses.length) {
      query += `&occurrenceStatus=${occurrenceStatuses
        .map((value) => encodeURIComponent(value))
        .join(',')}`;
    }
  }
  if (startDate) {
    query += `&startDate=${encodeURIComponent(startDate)}`;
  }
  if (endDate) {
    query += `&endDate=${encodeURIComponent(endDate)}`;
  }
  if (lboSearch) {
    query += `&lboSearch=${encodeURIComponent(lboSearch)}`;
  }
  if (bzkSearch) {
    query += `&bzkSearch=${encodeURIComponent(bzkSearch)}`;
  }
  if (phoneSearch) {
    query += `&phoneSearch=${encodeURIComponent(phoneSearch)}`;
  }
  if (renewStartDate) {
    query += `&renewStartDate=${encodeURIComponent(renewStartDate)}`;
  }
  if (renewEndDate) {
    query += `&renewEndDate=${encodeURIComponent(renewEndDate)}`;
  }
  if (userBirthdaySearch) {
    query += `&userBirthdaySearch=${encodeURIComponent(userBirthdaySearch)}`;
  }
  if (haveApp !== null && haveApp !== undefined && haveApp !== '') {
    query += `&haveApp=${encodeURIComponent(haveApp)}`;
  }
  if (
    isHealthBookletVerified !== null &&
    isHealthBookletVerified !== undefined &&
    isHealthBookletVerified !== ''
  ) {
    query += `&isHealthBookletVerified=${encodeURIComponent(
      isHealthBookletVerified
    )}`;
  }

  return query;
};

export const fetchTherapies = async ({
  page = 1,
  limit = 10,
  ...filters
}) => {
  try {
    // view=table: bek vraca samo ono sto tabela prikazuje (istaknutu aktivnost
    // umesto svih aktivnosti). Ostalo se dofetchuje na klik akcije.
    const query = `?page=${page}&limit=${limit}&view=table${buildTherapyFilterQuery(filters)}`;
    const response = await authClient.get(`/therapies${query}`);
    return {
      data: response.data,
      status: response.status,
      ok: isOkStatus(response.status),
      message: response.data?.message || null,
    };
  } catch (error) {
    const message = extractErrorMessage(
      error,
      'Doslo je do greske pri dobavljanju terapija.'
    );
    return {
      data: null,
      status: error?.response?.status || null,
      ok: false,
      message,
    };
  }
};

// "Selektuj sve": id + ime pacijenta za sve terapije koje odgovaraju filterima,
// jednim lakim zahtevom umesto ucitavanja svih strana liste.
// Vraca data: { therapies: [{ _id, userId, firstName, lastName }], totalCount }.
export const fetchTherapySelection = async (filters = {}) => {
  try {
    const query = buildTherapyFilterQuery(filters).replace(/^&/, '?');
    const response = await authClient.get(`/therapies/selection${query}`);
    return {
      data: response.data,
      status: response.status,
      ok: isOkStatus(response.status),
      message: response.data?.message || null,
    };
  } catch (error) {
    const message = extractErrorMessage(
      error,
      'Doslo je do greske pri selektovanju terapija.'
    );
    return {
      data: null,
      status: error?.response?.status || null,
      ok: false,
      message,
    };
  }
};

export const fetchDailyTherapyActivities = async ({
  date,
  page = 1,
  limit = 10,
  types = [],
}) => {
  try {
    const params = new URLSearchParams();

    params.set('date', date);
    params.set('page', String(page));
    params.set('limit', String(limit));

    if (Array.isArray(types) && types.length > 0) {
      params.set('type', types.join(','));
    }

    const response = await authClient.get(
      `/therapies/daily-activities?${params.toString()}`
    );
    return {
      data: response.data,
      status: response.status,
      ok: isOkStatus(response.status),
      message: response.data?.message || null,
    };
  } catch (error) {
    const message = extractErrorMessage(
      error,
      'Doslo je do greske pri dobavljanju dnevnih aktivnosti terapija.'
    );
    return {
      data: null,
      status: error?.response?.status || null,
      ok: false,
      message,
    };
  }
};

export const fetchUserTherapies = async ({
  userId,
  page = 1,
  limit = 10,
  search = null,
  status = null,
}) => {
  try {
    if (!userId) {
      return {
        data: null,
        status: null,
        ok: false,
        message: 'Korisnik nije izabran.',
      };
    }

    let query = `?page=${page}&limit=${limit}`;
    if (search) {
      query += `&search=${encodeURIComponent(search)}`;
    }
    if (status) {
      query += `&status=${encodeURIComponent(status)}`;
    }

    const response = await authClient.get(`/users/therapies/${userId}${query}`);
    return {
      data: response.data,
      status: response.status,
      ok: isOkStatus(response.status),
      message: response.data?.message || null,
    };
  } catch (error) {
    const message = extractErrorMessage(
      error,
      'Doslo je do greske pri dobavljanju terapija korisnika.'
    );
    return {
      data: null,
      status: error?.response?.status || null,
      ok: false,
      message,
    };
  }
};

export const fetchUserTherapyModalContext = async (userId) => {
  try {
    if (!userId) {
      return {
        data: null,
        status: null,
        ok: false,
        message: 'Korisnik nije izabran.',
      };
    }

    const response = await authClient.get(
      `/users/${userId}/therapy-modal-context`
    );
    return {
      data: response.data,
      status: response.status,
      ok: isOkStatus(response.status),
      message: response.data?.message || null,
    };
  } catch (error) {
    const message = extractErrorMessage(
      error,
      'Doslo je do greske pri dobavljanju konteksta terapije korisnika.'
    );
    return {
      data: null,
      status: error?.response?.status || null,
      ok: false,
      message,
    };
  }
};

export const fetchUserTherapyReservation = async (userId) => {
  try {
    if (!userId) {
      return {
        data: null,
        status: null,
        ok: false,
        message: 'Korisnik nije izabran.',
      };
    }

    const response = await authClient.get(
      `/users/${userId}/therapy-reservation`
    );
    return {
      data: response.data,
      status: response.status,
      ok: isOkStatus(response.status),
      message: response.data?.message || null,
    };
  } catch (error) {
    const message = extractErrorMessage(
      error,
      'Doslo je do greske pri dobavljanju rezervacije terapije korisnika.'
    );
    return {
      data: null,
      status: error?.response?.status || null,
      ok: false,
      message,
    };
  }
};

export const fetchAcuteTherapySuggestion = async (userId) => {
  try {
    if (!userId) {
      return {
        data: null,
        status: null,
        ok: false,
        message: 'Korisnik nije izabran.',
      };
    }

    const response = await authClient.get(
      `/users/${userId}/acute-therapy-suggestion`
    );
    return {
      data: response.data,
      status: response.status,
      ok: isOkStatus(response.status),
      message: response.data?.message || null,
    };
  } catch (error) {
    const message = extractErrorMessage(
      error,
      'Doslo je do greske pri dobavljanju predloga lekova.'
    );
    return {
      data: null,
      status: error?.response?.status || null,
      ok: false,
      message,
    };
  }
};

export const createTherapy = async (payload) => {
  try {
    const response = await authClient.post(`/therapies`, payload);
    return {
      data: response.data,
      status: response.status,
      ok: isOkStatus(response.status),
      message: response.data?.message || 'Terapija kreirana.',
    };
  } catch (error) {
    const message = extractErrorMessage(
      error,
      'Doslo je do greske pri kreiranju terapije.'
    );
    return {
      data: null,
      status: error?.response?.status || null,
      ok: false,
      message,
    };
  }
};

export const updateTherapy = async (id, payload) => {
  try {
    const response = await authClient.put(`/therapies/${id}`, payload);
    return {
      data: response.data,
      status: response.status,
      ok: isOkStatus(response.status),
      message: response.data?.message || 'Terapija azurirana.',
    };
  } catch (error) {
    const message = extractErrorMessage(
      error,
      'Doslo je do greske pri azuriranju terapije.'
    );
    return {
      data: null,
      status: error?.response?.status || null,
      ok: false,
      message,
    };
  }
};

export const updateTherapyTemplate = async (id, payload) => {
  try {
    const response = await authClient.put(`/therapies/${id}`, payload);
    return {
      data: response.data,
      status: response.status,
      ok: isOkStatus(response.status),
      message: response.data?.message || 'Terapija azurirana.',
    };
  } catch (error) {
    const message = extractErrorMessage(
      error,
      'Doslo je do greske pri azuriranju terapije.'
    );
    return {
      data: null,
      status: error?.response?.status || null,
      ok: false,
      message,
      errorCode:
        error?.response?.data?.code ||
        error?.response?.data?.errorCode ||
        error?.response?.data?.error,
    };
  }
};

export const renewTherapy = async (id, payload) => {
  try {
    const response = await authClient.post(`/therapies/renew/${id}`, payload);
    return {
      data: response.data,
      status: response.status,
      ok: isOkStatus(response.status),
      message: response.data?.message || 'Terapija obnovljena.',
    };
  } catch (error) {
    const message = extractErrorMessage(
      error,
      'Doslo je do greske pri obnovi terapije.'
    );
    return {
      data: null,
      status: error?.response?.status || null,
      ok: false,
      message,
    };
  }
};

export const upsertTherapyReservation = async (therapyId, payload) => {
  try {
    const response = await authClient.put(
      `/therapies/${therapyId}/reservation`,
      payload
    );
    return {
      data: response.data,
      status: response.status,
      ok: isOkStatus(response.status),
      message: response.data?.message || 'Rezervacija je sacuvana.',
    };
  } catch (error) {
    const message = extractErrorMessage(
      error,
      'Doslo je do greske pri cuvanju rezervacije.'
    );
    return {
      data: null,
      status: error?.response?.status || null,
      ok: false,
      message,
    };
  }
};

export const deleteTherapyReservation = async (therapyId) => {
  try {
    const response = await authClient.delete(
      `/therapies/${therapyId}/reservation`
    );
    return {
      data: response.data,
      status: response.status,
      ok: isOkStatus(response.status),
      message: response.data?.message || 'Rezervacija je obrisana.',
    };
  } catch (error) {
    const message = extractErrorMessage(
      error,
      'Doslo je do greske pri brisanju rezervacije.'
    );
    return {
      data: null,
      status: error?.response?.status || null,
      ok: false,
      message,
    };
  }
};

export const activateTherapyReservation = async (therapyId) => {
  try {
    const response = await authClient.post(
      `/therapies/${therapyId}/reservation/activate`
    );
    return {
      data: response.data,
      status: response.status,
      ok: isOkStatus(response.status),
      message: response.data?.message || 'Rezervacija je aktivirana.',
    };
  } catch (error) {
    const message = extractErrorMessage(
      error,
      'Doslo je do greske pri aktivaciji rezervacije.'
    );
    return {
      data: null,
      status: error?.response?.status || null,
      ok: false,
      message,
    };
  }
};

export const fetchTherapyTerminationPreview = async (id) => {
  try {
    const response = await authClient.get(
      `/therapies/${id}/termination-preview`
    );
    return {
      data: response.data,
      status: response.status,
      ok: isOkStatus(response.status),
      message: response.data?.message || null,
    };
  } catch (error) {
    const message = extractErrorMessage(
      error,
      'Doslo je do greske pri dobavljanju pregleda za prekid terapije.'
    );
    return {
      data: null,
      status: error?.response?.status || null,
      ok: false,
      message,
    };
  }
};

export const terminateTherapy = async (id, payload) => {
  try {
    const response = await authClient.post(
      `/therapies/${id}/terminate`,
      payload
    );
    return {
      data: response.data,
      status: response.status,
      ok: isOkStatus(response.status),
      message: response.data?.message || 'Terapija je prekinuta.',
    };
  } catch (error) {
    const message = extractErrorMessage(
      error,
      'Doslo je do greske pri prekidu terapije.'
    );
    return {
      data: null,
      status: error?.response?.status || null,
      ok: false,
      message,
    };
  }
};

export const bulkRenewTherapies = async ({
  ids,
  centralDate = null,
  durationMonths = null,
}) => {
  try {
    const payload = { ids };

    if (centralDate) {
      payload.centralDate = centralDate;
    }

    if (durationMonths) {
      payload.durationMonths = durationMonths;
    }

    const response = await authClient.post('/therapies/bulk-renew', payload);
    return {
      data: response.data,
      status: response.status,
      ok: isOkStatus(response.status),
      message: response.data?.message || 'Terapije obnovljene.',
    };
  } catch (error) {
    const message = extractErrorMessage(
      error,
      'Doslo je do greske pri grupnoj obnovi terapija.'
    );
    return {
      data: null,
      status: error?.response?.status || null,
      ok: false,
      message,
    };
  }
};

export const bulkUpdateTherapyStatus = async ({ ids, status }) => {
  try {
    const response = await authClient.patch('/therapies/bulk-status', {
      ids,
      status,
    });
    return {
      data: response.data,
      status: response.status,
      ok: isOkStatus(response.status),
      message: response.data?.message || 'Statusi terapija ažurirani.',
    };
  } catch (error) {
    const message = extractErrorMessage(
      error,
      'Doslo je do greske pri masovnom azuriranju statusa terapija.'
    );
    return {
      data: null,
      status: error?.response?.status || null,
      ok: false,
      message,
    };
  }
};

export const importTherapiesFromExcel = async (file) => {
  try {
    const fileBase64 = await toBase64(file);
    const response = await authClient.post('/therapies/import', {
      fileBase64,
    });

    return {
      data: response.data,
      status: response.status,
      ok: isOkStatus(response.status),
      message: response.data?.message || 'Terapije importovane.',
    };
  } catch (error) {
    const message = extractErrorMessage(
      error,
      'Doslo je do greske pri importu terapija.'
    );
    return {
      data: error?.response?.data || null,
      status: error?.response?.status || null,
      ok: false,
      message,
    };
  }
};

const parseFileName = (contentDisposition) => {
  if (!contentDisposition) return null;
  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) return decodeURIComponent(utf8Match[1]);
  const asciiMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
  return asciiMatch?.[1] || null;
};

export const exportTherapiesToExcel = async ({ ids }) => {
  try {
    const response = await authClient.post(
      '/therapies/export',
      { ids },
      { responseType: 'blob' }
    );

    return {
      data: response.data,
      fileName:
        parseFileName(response.headers?.['content-disposition']) ||
        `therapies-export-${Date.now()}.xlsx`,
      status: response.status,
      ok: isOkStatus(response.status),
      message: null,
    };
  } catch (error) {
    const message = extractErrorMessage(
      error,
      'Doslo je do greske pri eksportu terapija.'
    );
    return {
      data: null,
      fileName: null,
      status: error?.response?.status || null,
      ok: false,
      message,
    };
  }
};
